import {
  Dialog,
  DialogTitle,
  Typography,
  Button,
  IconButton,
  Icon,
  DialogContent,
  DialogContentText,
  Box,
} from "@material-ui/core";
import Alert, { Color } from "@material-ui/lab/Alert";
import { openModal, closeModal } from "actions/modalActions";
import { useTypeSelectors } from "hooks/useTypeSelectors";
import { FC, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useDialogStyles } from "components/modals/styles";
import { useStyles } from "./styles";

interface ModalWindowProps {
  buttonType: "icon" | "text" | "custom" | "none";
  children: any;
  modal_name: string;
  fullWidth?: boolean;
  buttonText?: string;
  modalTitle?: string;
  modalDescription?: string;
  modalText?: string;
  theme?: string;
  hasIcon?: boolean;
  icon?: JSX.Element | undefined;
  isDisabled?: boolean;
  isModalButton?: boolean;
  defaultTitleColor?: boolean;
  customIconClass?: any;
  customButtonElement?: any;
  customOnClick?: any;
}

interface TextModalButtonProps {
  buttonText: string | undefined;
  onClick: () => void;
  fullWidth?: boolean;
  isModalButton?: boolean;
  isDisabled?: boolean | undefined;
  extraClass?: string;
}

interface IconModalButtonProps {
  icon: JSX.Element | undefined;
  onClick: () => void;
  modalParam: {
    colorClass: string;
    backgroundClass: string;
    severity: string;
  };
  customIconClass: any;
}

export const ModalWindow: FC<ModalWindowProps> = ({
  buttonType,
  fullWidth,
  children,
  modal_name,
  buttonText,
  modalTitle,
  modalDescription,
  modalText,
  theme,
  hasIcon,
  icon,
  isDisabled,
  isModalButton,
  defaultTitleColor,
  customIconClass,
  customButtonElement,
  customOnClick,
}) => {
  const classes = useStyles();

  const [modalParam, setModalParam] = useState({
    colorClass: classes.blueColor,
    backgroundClass: classes.blueBackground,
    severity: "info",
  });

  useEffect(() => {
    switch (theme) {
      case "green":
        setModalParam({
          colorClass: classes.greenColor,
          backgroundClass: classes.greenBackground,
          severity: "success",
        });
        break;
      case "yellow":
        setModalParam({
          colorClass: classes.yellowColor,
          backgroundClass: classes.yellowBackground,
          severity: "wanring",
        });
        break;
      case "red":
        setModalParam({
          colorClass: classes.redColor,
          backgroundClass: classes.redBackground,
          severity: "danger",
        });
        break;
      case "blue":
        setModalParam({
          colorClass: classes.blueColor,
          backgroundClass: classes.blueBackground,
          severity: "info",
        });
        break;
      default:
        setModalParam({
          colorClass: classes.grayColor,
          backgroundClass: classes.grayBackground,
          severity: "",
        });
    }
  }, [
    theme,
    classes.blueBackground,
    classes.blueColor,
    classes.greenBackground,
    classes.greenColor,
    classes.redBackground,
    classes.redColor,
    classes.yellowBackground,
    classes.yellowColor,
    classes.grayColor,
    classes.grayBackground,
  ]);

  const dispatch = useDispatch();

  const { isOpen, modalName } = useTypeSelectors((state) => state.modalState);

  const triggerOnClick= () => {
    if (!customOnClick) {
      dispatch(openModal(modal_name));
    }else{
      dispatch(customOnClick);
    }
  }

  return (
    <>
      {buttonType === "text" ? (
        <TextModalButton
          fullWidth={fullWidth}
          buttonText={buttonText || modalTitle}
          isModalButton={isModalButton}
          onClick={triggerOnClick}
          isDisabled={isDisabled}
          extraClass={theme === "red" ? classes.deleteBtn : ""}
        />
      ) : buttonType === "icon" ? (
        <IconModalButton
          icon={icon}
          onClick={triggerOnClick}
          modalParam={modalParam}
          customIconClass={customIconClass}
        />
      ) : buttonType === "custom" ? (
        <Box
          className={`${classes.customButtonElement} ${isDisabled ? classes.disabledButton : ""}`}
          onClick={triggerOnClick}
        >
          {customButtonElement}
        </Box>
      ) : (
        ""
      )}
      <Dialog
        className={classes.root}
        open={isOpen && modalName === modal_name}
        onClose={() => dispatch(closeModal())}
        BackdropProps={{
          classes: {
            root: classes.backDrop,
          },
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle className={classes.dialogTitle}>
          {hasIcon && (
            <Alert
              severity={modalParam.severity as Color}
              icon={false}
              className={`${classes.iconBox} ${modalParam.colorClass} ${modalParam.backgroundClass}`}
            >
              <Icon style={{ fontSize: 60 }} className={classes.modalIcon}>
                {icon}
              </Icon>
            </Alert>
          )}
          <Typography
            className={`${classes.title} ${classes.iconBtn}  ${!defaultTitleColor ? modalParam.colorClass : classes.grayColor}`}
            variant="h4"
            id="alert-dialog-title"
          >
            {modalTitle}
          </Typography>
          <Typography
            className={`${classes.subTitle} ${modalParam.colorClass}`}
            variant="h5"
            id="alert-dialog-description"
          >
            {modalDescription}
          </Typography>
        </DialogTitle>

        {modalText && (
          <DialogContent>
            <DialogContentText id="alert-dialog-description">{modalText}</DialogContentText>
          </DialogContent>
        )}

        {children}
      </Dialog>
    </>
  );
};

export const TextModalButton: FC<TextModalButtonProps> = ({
  fullWidth,
  buttonText,
  isModalButton,
  onClick,
  isDisabled,
  extraClass,
}) => {
  const dialogClasses = useDialogStyles();
  const classes = useStyles();

  return (
    <Button
      fullWidth={fullWidth}
      data-testid="triggerTextButtonToOpenModal"
      className={`${classes.button} ${isModalButton && dialogClasses.actionButton} ${extraClass}`}
      variant="contained"
      color="primary"
      onClick={onClick}
      disabled={isDisabled}
    >
      {buttonText}
    </Button>
  );
};

export const IconModalButton: FC<IconModalButtonProps> = ({
  icon,
  onClick,
  modalParam,
  customIconClass,
}) => {
  const classes = useStyles();

  return (
    <IconButton
      data-testid="triggerIconButtonToOpenModal"
      className={`${classes.iconBtn} ${modalParam?.colorClass} ${customIconClass ? customIconClass : modalParam?.backgroundClass}`}
      onClick={onClick}
      color="primary"
    >
      <Icon>{icon}</Icon>
    </IconButton>
  );
};
